import React, { FunctionComponent, Fragment } from 'react';
import { usePassportContext } from '@tti/passport';
import { Api, Models, States } from '@core/types';
import { connect } from 'react-redux';
import { Modal } from '@components/shared';
import { PassportEnums } from '@tti/passport';
import PdfSettings from './PdfSettings';
import { ProjectEnums } from '@core/enums';
import { PDFItemType, PDFType } from '@core/enums/project';
import { PDFProps } from '@core/types/api';
import { pdfSettingsActionCreators } from '@core/redux/pdfSettings';
import { FormattedNumberConfig, FormValue } from '@core/types/models';
import { useDispatch } from 'react-redux';
import { handleClose } from '@core/redux/pdfSettings/actioncreators';

interface IProps {
  app?: States.IAppState;
  project?: States.IProjectState;
  pdfSettings: States.IPdfSettingsState;
  product: States.IProductState;
  generatePDF: typeof pdfSettingsActionCreators.generatePDF;
  sendForApproval: typeof pdfSettingsActionCreators.SendForApproval;
  setPDFModal: typeof pdfSettingsActionCreators.setPDFModal;
  downloadPDF: typeof pdfSettingsActionCreators.downloadPDF;
  clearPDF: typeof pdfSettingsActionCreators.clearPDF;
  setPdfItemType: typeof pdfSettingsActionCreators.setPdfItemType;
  setCurrency: typeof pdfSettingsActionCreators.setCurrency;
  setField: typeof pdfSettingsActionCreators.setField;
  handleClose: typeof pdfSettingsActionCreators.handleClose;
}

const ProjectDetailFooter: FunctionComponent<IProps> = ({
  app,
  project,
  pdfSettings,
  product,
  generatePDF,
  sendForApproval,
  setPDFModal,
  downloadPDF,
  setPdfItemType,
  setCurrency,
  setField,
}) => {
  const dispatch = useDispatch();
  const { passportContext, getClaim, getClaims } = usePassportContext();
  const cultureClaim = getClaim(PassportEnums.ClaimType.Locality, passportContext.claims);
  const allCultureClaims = getClaims(PassportEnums.ClaimType.Role, passportContext.claims).filter((x) =>
    x.value.startsWith(PassportEnums.RoleType.SentToPrintApprover),
  );

  const handleShowGeneratePdf = (type: ProjectEnums.PDFType) => {
    setPDFModal({
      pdfType: type,
      isOpen: true,
      showConfirmation: false,
    });
  };

  const handleGeneratePDF = async (props: PDFProps) => {
    if (!project || project.currentProject === null || !passportContext.bearerToken) {
      return;
    }

    generatePDF(
      {
        bearerToken: passportContext.bearerToken,
        projectGUID: project.currentProject.projectGUID,
        flyerData: props,
        isPrint: props.type === ProjectEnums.PDFType.SendToPrinters,
        templateName: props.template.value,
      },
      props.type,
    );
  };

  if (!project || project.currentProject === null || project.currentProject.products.length === 0 || !app) {
    return null;
  }

  const handleDownload = () => {
    if (!pdfSettings.downloadPDF.projectPdf) {
      return;
    }

    downloadPDF(pdfSettings.downloadPDF.projectPdf.pdfUrl);

    dispatch(handleClose(PDFType.Download));
  };

  const handleSendToApproval = () => {
    if (
      !project ||
      project.currentProject === null ||
      !passportContext.bearerToken ||
      !cultureClaim ||
      !pdfSettings.printPDF.projectPdf
    ) {
      return;
    }

    const userCultures = allCultureClaims.map((x) => {
      // example: role.ftg.sent-to-print-approver.en-gb
      // Get the last index of '.'
      const index = x.value.lastIndexOf('.');

      // + 1 to skip the last '.' and return the culture code
      return x.value.substring(index + 1, x.value.length);
    });

    sendForApproval(
      {
        bearerToken: passportContext.bearerToken,
        cultureCode: cultureClaim.value,
        projectPDFID: pdfSettings.printPDF.projectPdf.projectPDFID,
      },
      project.currentProject.projectGUID,
      userCultures,
    );

    handleClose(PDFType.SendToPrinters);
  };

  return (
    <Fragment>
      <Modal
        visible={pdfSettings.printPDF.modal.isOpen}
        onClose={() => dispatch(handleClose(PDFType.SendToPrinters))}
        showClose={!pdfSettings.downloadPDF.modal.isGenerating}
      >
        <PdfSettings
          pdf={pdfSettings.printPDF}
          handleGeneratePDF={handleGeneratePDF}
          onSubmit={handleSendToApproval}
          onClose={() => dispatch(handleClose(PDFType.SendToPrinters))}
          type={PDFType.SendToPrinters}
          isPrint={true}
        />
      </Modal>
      <Modal
        visible={pdfSettings.downloadPDF.modal.isOpen}
        onClose={() => dispatch(handleClose(PDFType.Download))}
        showClose={!pdfSettings.downloadPDF.modal.isGenerating}
      >
        <PdfSettings
          pdf={pdfSettings.downloadPDF}
          onSubmit={handleDownload}
          onClose={() => dispatch(handleClose(PDFType.Download))}
          handleGeneratePDF={handleGeneratePDF}
          type={PDFType.Download}
          isPrint={false}
        />
      </Modal>

      <div className="selected-products__footer">
        <button
          className="btn btn--inline selected-products__download"
          onClick={() => handleShowGeneratePdf(ProjectEnums.PDFType.Download)}
        >
          Download
        </button>
        <button
          className="btn btn--inline selected-products__download"
          onClick={() => handleShowGeneratePdf(ProjectEnums.PDFType.SendToPrinters)}
        >
          Send To Printers
        </button>
      </div>
    </Fragment>
  );
};

const mapStateToProps = (state: States.IRootState) => ({
  app: state.app,
  project: state.project,
  pdfSettings: state.pdfSettings,
  product: state.product,
});

const mapDispatchToProps = (dispatch: any) => ({
  generatePDF: (params: Api.IUpdateFlyerAndGeneratePDFRequest, type: PDFType) =>
    dispatch(pdfSettingsActionCreators.generatePDF(params, type)),
  sendForApproval: (params: Api.ISendForApprovalRequest, projectGuid: string, userCultures: string[]) =>
    dispatch(pdfSettingsActionCreators.SendForApproval(params, projectGuid, userCultures)),
  setPDFModal: (params: Models.SetPDFModal) => dispatch(pdfSettingsActionCreators.setPDFModal(params)),
  downloadPDF: (url: string) => dispatch(pdfSettingsActionCreators.downloadPDF(url)),
  clearPDF: (type: ProjectEnums.PDFType) => dispatch(pdfSettingsActionCreators.clearPDF(type)),
  setPdfItemType: (pdfItemType: PDFItemType | null) => dispatch(pdfSettingsActionCreators.setPdfItemType(pdfItemType)),
  setCurrency: (currency: FormattedNumberConfig) => dispatch(pdfSettingsActionCreators.setCurrency(currency)),
  setField: (field: string, value: FormValue) => dispatch(pdfSettingsActionCreators.setField(field, value)),
  handleClose: (type: PDFType) => dispatch(pdfSettingsActionCreators.handleClose(type)),
});

export default connect(mapStateToProps, mapDispatchToProps)(ProjectDetailFooter);
